<template>
  <div>
    <section class="space-y-4">
      <div class="flex items-center space-x-10">
        <div class="w-100">
          <p class="text-background-fg/80 text-sm">Send the companion link</p>
          <p
            v-if="booking.customer?.phone"
            class="text-background-fg/50 text-xs"
          >
            Texts an Arrival Companion login link, where the customer can open a
            compound gate with a tap instead of their PIN.
          </p>
          <p v-else class="text-background-fg/50 text-xs">
            The customer has no mobile number. Add one on the Details tab first.
          </p>
        </div>

        <button
          :class="[
            ringButton,
            'ring-secondary text-secondary hover:bg-secondary/10',
          ]"
          type="button"
          :disabled="busy || !booking.customer?.phone"
          @click="onSendLink"
        >
          Send Companion Link
        </button>
      </div>

      <div class="flex items-center space-x-10">
        <div class="w-100">
          <p class="text-background-fg/80 text-sm">Renew this booking</p>
          <p class="text-background-fg/50 text-xs">
            Moves the end date on and texts the customer their welcome message.
          </p>
        </div>

        <button
          :class="[
            ringButton,
            'ring-secondary text-secondary hover:bg-secondary/10',
          ]"
          type="button"
          :disabled="busy || booking.cancelled"
          @click="onRenew"
        >
          Renew
        </button>
      </div>

      <div class="flex items-center space-x-10">
        <div class="w-100">
          <p class="text-background-fg/80 text-sm">
            Mark the {{ unitLabel }} as {{ otherLocation }}
          </p>
          <p class="text-background-fg/50 text-xs">
            When the gates got it wrong, for example after a tow-out by the site
            team. The gates work from this until the next crossing.
          </p>
        </div>

        <button
          :class="[ringButton, 'ring-primary text-primary hover:bg-primary/10']"
          type="button"
          :disabled="busy"
          @click="onSetLocation"
        >
          Mark as {{ otherLocation === "in" ? "In" : "Out" }}
        </button>
      </div>

      <div class="flex items-center space-x-10">
        <div class="w-100">
          <p class="text-background-fg/80 text-sm">Issue a new PIN</p>
          <p class="text-background-fg/50 text-xs">
            For a PIN that has been lost or shared. The current one stops
            working at once, on every storage booking this customer has.
          </p>
        </div>

        <button
          :class="[ringButton, 'ring-amber text-amber hover:bg-amber/10']"
          type="button"
          :disabled="busy || !booking.customer"
          @click="onRegeneratePin"
        >
          New PIN
        </button>
      </div>

      <div class="flex items-center space-x-10">
        <div class="w-100">
          <p class="text-background-fg/80 text-sm">End this booking today</p>
          <p class="text-background-fg/50 text-xs">
            The customer can no longer drive in. They can still take their
            {{ unitLabel }} out, confirming with their PIN as usual.
          </p>
        </div>

        <button
          :class="[ringButton, 'ring-amber text-amber hover:bg-amber/10']"
          type="button"
          :disabled="busy || booking.cancelled || booking.ended"
          @click="onExpire"
        >
          End Now
        </button>
      </div>

      <div class="flex items-center space-x-10">
        <div class="w-100">
          <p class="text-background-fg/80 text-sm">Cancel this booking</p>
          <p class="text-background-fg/50 text-xs">
            For a booking made by mistake or an agreement that has been
            terminated. This action cannot be undone.
          </p>
        </div>

        <button
          :class="[ringButton, 'ring-error text-error hover:bg-error/10']"
          type="button"
          :disabled="busy || booking.cancelled"
          @click="onCancel"
        >
          Cancel Booking
        </button>
      </div>
    </section>
  </div>

  <BaseConfirmation
    ref="sendLinkConfirmRef"
    :description="`Text ${booking.customer?.phone ?? 'the customer'} a fresh Arrival Companion login link?`"
    confirm-label="Send link"
    cancel-label="Cancel"
    type="positive"
  />

  <BaseConfirmation
    ref="renewConfirmRef"
    description="Choose the new end date. The customer is texted their welcome message."
    confirm-label="Renew"
    cancel-label="Cancel"
    type="positive"
    :confirm-disabled="!renewTo"
  >
    <BaseInputText v-model="renewTo" type="date" label="New end date" />
  </BaseConfirmation>

  <BaseConfirmation
    ref="locationConfirmRef"
    :description="`Mark the ${unitLabel} as ${otherLocation}? Say why: it is recorded with your name.`"
    :confirm-label="`Mark as ${otherLocation}`"
    cancel-label="Cancel"
    type="positive"
    :confirm-disabled="!reason.trim()"
  >
    <BaseInputTextarea v-model="reason" label="Reason" />
  </BaseConfirmation>

  <BaseConfirmation
    ref="pinConfirmRef"
    description="Issue a new PIN? The customer's current PIN stops working straight away, at every gate."
    confirm-label="Issue new PIN"
    cancel-label="Cancel"
  />

  <BaseConfirmation
    ref="newPinRef"
    title="New PIN issued"
    description="Give the customer their new PIN. The old one no longer works."
    confirm-label="Done"
    cancel-label=""
    type="positive"
  >
    <BaseInputField label="Gate PIN">
      <p class="text-lg font-semibold tabular-nums">{{ newPin }}</p>
    </BaseInputField>
  </BaseConfirmation>

  <BaseConfirmation
    ref="expireConfirmRef"
    description="End this booking today? The customer will be refused entry from now on. Leaving stays allowed."
    confirm-label="End now"
    cancel-label="Cancel"
  />

  <BaseConfirmation
    ref="cancelConfirmRef"
    description="Are you sure you wish to cancel this booking? The pitch becomes free and the customer's plates stop opening the gates."
    confirm-label="Yes, cancel booking"
    cancel-label="No, don't cancel booking"
  />
</template>

<script setup lang="ts">
import BaseConfirmation from "./BaseConfirmation.vue";
import { ApiError } from "@/api";
import { cancelStorageBooking } from "@/services/storage/cancelStorageBooking";
import { expireStorageBooking } from "@/services/storage/expireStorageBooking";
import { regenerateStoragePin } from "@/services/storage/regenerateStoragePin";
import { renewStorageBooking } from "@/services/storage/renewStorageBooking";
import { sendStorageCompanionLink } from "@/services/storage/sendStorageCompanionLink";
import { setStorageLocation } from "@/services/storage/setStorageLocation";
import {
  STORAGE_KIND_LABELS,
  defaultStorageRenewalDate,
  type StorageBooking,
} from "@/services/storage/storage";

const props = defineProps<{ booking: StorageBooking }>();

const ringButton =
  "rounded px-2 py-1 ring-2 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-inherit";

type Confirmation = InstanceType<typeof BaseConfirmation>;
const sendLinkConfirmRef = ref<Confirmation | null>(null);
const renewConfirmRef = ref<Confirmation | null>(null);
const locationConfirmRef = ref<Confirmation | null>(null);
const pinConfirmRef = ref<Confirmation | null>(null);
const newPinRef = ref<Confirmation | null>(null);
const expireConfirmRef = ref<Confirmation | null>(null);
const cancelConfirmRef = ref<Confirmation | null>(null);

const unitLabel = computed(() =>
  props.booking.kind === "other"
    ? "unit"
    : STORAGE_KIND_LABELS[props.booking.kind].toLowerCase(),
);
const otherLocation = computed(() =>
  props.booking.location === "in" ? "out" : "in",
);

const busy = ref(false);

/** Confirm in the dialog, then run the action and toast the outcome. */
const run = async (
  confirmation: Confirmation | null,
  action: () => Promise<unknown>,
  done: string,
) => {
  const { isCanceled } = (await confirmation?.reveal()) ?? { isCanceled: true };
  if (isCanceled) return;
  busy.value = true;
  try {
    await action();
    toast.success(done);
  } catch (error) {
    if (!(error instanceof ApiError)) throw error;
    toast.error(error.detail ?? "That did not work. Try again.");
  } finally {
    busy.value = false;
  }
};

const onSendLink = () =>
  run(
    sendLinkConfirmRef.value,
    () => sendStorageCompanionLink(props.booking.bookingId),
    "Companion link sent",
  );

const renewTo = ref("");
const onRenew = () => {
  renewTo.value = defaultStorageRenewalDate(props.booking.endDate);
  return run(
    renewConfirmRef.value,
    () => renewStorageBooking(props.booking.bookingId, renewTo.value),
    "Booking renewed",
  );
};

const reason = ref("");
const onSetLocation = () => {
  reason.value = "";
  const target = otherLocation.value;
  return run(
    locationConfirmRef.value,
    () => setStorageLocation(props.booking.bookingId, target, reason.value),
    `Marked as ${target}`,
  );
};

// Shown once, in the dialog that follows: never kept in the query cache.
const newPin = ref("");
const onRegeneratePin = () =>
  run(
    pinConfirmRef.value,
    async () => {
      newPin.value = await regenerateStoragePin(props.booking.bookingId);
      void newPinRef.value?.reveal().then(() => (newPin.value = ""));
    },
    "New PIN issued",
  );

const onExpire = () =>
  run(
    expireConfirmRef.value,
    () => expireStorageBooking(props.booking.bookingId),
    "Booking ended",
  );
const onCancel = () =>
  run(
    cancelConfirmRef.value,
    () => cancelStorageBooking(props.booking.bookingId),
    "Booking cancelled",
  );
</script>
